<template>
    <div component="Bible" template="page">
        <div class="bg">
            <v-card class="holder">
                <div class="filter">
                    <v-text-field v-model="search" class="search" clearable hide-details label="Search" />

                    <div class="action cell">
                        <btn icon="add_circle" text="ADD BOOK" color="base" @click="openDialog('book')"></btn>
                        <btn icon="add_circle" text="ADD CHAPTER" color="base" @click="openDialog('chapter')"></btn>
                    </div>
                </div>

                <div class="loading" v-if="loading">
                    <v-progress-circular indeterminate size="28" width="3" />
                    <span>Loading bible...</span>
                </div>

                <div class="columns" v-else>
                    <div class="books">
                        <table class="list">
                            <thead>
                                <tr>
                                    <th>Book</th>
                                    <th class="num">Chapters</th>
                                    <th class="edit"></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr :class="{ selected: !selectedBook }" @click="selectedBook = null">
                                    <td><strong>All books</strong></td>
                                    <td class="num">{{ chapters.length }}</td>
                                    <td class="edit"></td>
                                </tr>
                                <tr
                                    v-for="book in books"
                                    :key="book.id"
                                    :class="{ selected: selectedBook === book.id }"
                                    @click="selectedBook = book.id"
                                >
                                    <td>{{ book.caption }}</td>
                                    <td class="num">{{ book.chapters_count }}</td>
                                    <td class="edit">
                                        <i class="icon" title="Edit book" @click.stop="openDialog('book', book)"
                                            >edit</i
                                        >
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div class="chapters">
                        <table class="list">
                            <thead>
                                <tr>
                                    <th>Chapter</th>
                                    <th>Book</th>
                                    <th class="num">Actors</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-if="!filteredChapters.length">
                                    <td colspan="3" class="empty">No chapters found.</td>
                                </tr>
                                <tr
                                    v-for="chapter in filteredChapters"
                                    :key="chapter.id"
                                    @click="openDialog('chapter', chapter)"
                                >
                                    <td>{{ chapter.caption }}</td>
                                    <td class="nowrap">{{ bookName(chapter.id_book) }}</td>
                                    <td class="num">{{ chapter.actors_count }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </v-card>
        </div>

        <bible-dialog
            v-model="dialog.open"
            :type="dialog.type"
            :record="dialog.record"
            :books="books"
            :default-book="selectedBook"
            @saved="onSaved"
            @deleted="onDeleted"
        />
    </div>
</template>

<script>
import { ABibleApi } from '@/api';
import BibleDialog from '@/components/Bible/BibleDialog.vue';

const normalizeText = (text) =>
    (text || '')
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();

export default {
    name: 'Bible',

    components: {
        BibleDialog,
    },

    data: () => ({
        loading: true,
        books: [],
        chapters: [],
        selectedBook: null,
        search: '',
        dialog: {
            open: false,
            type: 'book',
            record: null,
        },
    }),

    async created() {
        await this.load();
    },

    methods: {
        async load() {
            try {
                const { data } = await ABibleApi.load();
                this.books = data.books;
                this.chapters = data.chapters;
            } finally {
                this.loading = false;
            }
        },

        bookName(id) {
            const book = this.books.find((b) => b.id === id);
            return book ? book.caption : '';
        },

        openDialog(type, record = null) {
            this.dialog = { open: true, type, record };
        },

        // reload so chapter counts and ordering stay in sync
        async onSaved() {
            await this.load();
        },

        async onDeleted(record) {
            if (this.dialog.type === 'book' && this.selectedBook === record.id) {
                this.selectedBook = null;
            }
            await this.load();
        },
    },

    computed: {
        filteredChapters() {
            const parts = normalizeText(this.search).split(' ').filter(Boolean);

            return this.chapters.filter((chapter) => {
                if (this.selectedBook && chapter.id_book !== this.selectedBook) {
                    return false;
                }

                const haystack = normalizeText(`${chapter.caption} ${this.bookName(chapter.id_book)}`);
                return parts.every((part) => haystack.includes(part));
            });
        },
    },
};
</script>

<style lang="less" scoped>
@import '~@variables';

[component='Bible'] {
    height: 100vh;
    max-height: 100vh;

    .holder {
        min-height: 100%;
        display: flex;
        flex-direction: column;
        padding: 20px 20px 10px;
        .radiusBig !important;
    }

    .filter {
        display: flex;
        align-items: center;
        gap: 20px;
        padding: 10px 0;
        width: 100%;

        .search {
            flex: 0 1 400px;

            /deep/ .v-input__slot {
                padding-left: 15px !important;
            }
        }
    }

    .action {
        margin-left: auto;
        display: flex;
        align-items: center;
        gap: 10px;
    }

    .loading {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 12px;
        min-height: 200px;
    }

    .columns {
        display: flex;
        gap: 30px;
        height: 0;
        flex-grow: 1;
        margin-top: 10px;

        .books {
            width: 35%;
            overflow-y: auto;
        }

        .chapters {
            flex: 1;
            overflow-y: auto;
        }
    }

    .list {
        width: 100%;
        border-collapse: collapse;

        th {
            position: sticky;
            top: 0;
            background: white;
            text-align: left;
            font-size: 12px;
            padding: 10px 16px;
            border-bottom: 2px solid #999;
            white-space: nowrap;
        }

        td {
            font-size: 14px;
            padding: 12px 16px;
            border-bottom: 1px solid #e0e0e0;
        }

        tbody tr {
            cursor: pointer;
            transition: 0.2s;

            &:hover td {
                background: #f5f5f5;
            }

            &.selected td {
                background: fade(@base, 10%);
            }
        }

        .num {
            text-align: center;
            width: 90px;
        }

        .nowrap {
            white-space: nowrap;
        }

        .edit {
            width: 40px;
            text-align: right;

            .icon {
                font-size: 18px;
                color: #777;

                &:hover {
                    color: @dark;
                }
            }
        }

        .empty {
            text-align: center;
            color: #888;
            cursor: default;
        }
    }
}
</style>
